import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { BookOpen, Loader2, Lock, MessageCircle } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useModules } from '../../context/FeatureContext';
import { CopyButton, ErrorState, SectionCard, StatusPill, Switch, TableSkeleton } from '../../components/ui';

// Website chat: the switch that shows the chat widget on an organisation's website, and the code
// that puts it there. It used to be a page of its own (/chatbot). It now lives in Settings → AI
// receptionist, and web-chat leads are the "Web chat" channel in the receptionist's lead list.
//
//   <WebsiteChatSection hideWhenLocked />   Settings → AI receptionist (hidden without the module)
//   <WebsiteChatSection bare />             inside a dialog: no card or title of its own
//
// Self-contained: it reads /receptionist/my-config itself (or takes `config` when the page already
// has it) and saves the switch straight away with PUT /receptionist/my-config { chatbot_enabled }.
// Super admins see Rostery's own global widget instead, which has no organisation key.

const WIDGET_SRC = 'https://app.rostery.com.au/api/receptionist/widget.js';
const GLOBAL_SNIPPET = `<script src="${WIDGET_SRC}" async></script>`;
export const embedSnippetFor = (widgetKey) => (widgetKey ? `<script src="${WIDGET_SRC}?key=${widgetKey}" async></script>` : '');

// The embed snippet, shown in full so the key can be checked by eye: it wraps, stops at four
// lines, and offers "Show all" only when there is more to see.
export function EmbedCode({ snippet, className }) {
  const preRef = useRef(null);
  const codeId = useId();
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  useLayoutEffect(() => {
    const el = preRef.current;
    if (!el || expanded) return undefined;
    const measure = () => setClamped(el.scrollHeight - el.clientHeight > 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [snippet, expanded]);

  return (
    <div className={clsx('overflow-hidden rounded-lg border border-slate-200 bg-slate-50', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white pl-3 pr-1.5">
        <span className="text-[12px] font-medium text-slate-500">HTML</span>
        <CopyButton value={snippet} label="Copy code" title="Copy embed code" size={14}
          className="h-9 !px-2.5 !text-slate-600 hover:!text-slate-900" />
      </div>
      <pre id={codeId} ref={preRef}
        className={clsx('m-0 whitespace-pre-wrap break-all px-3 py-2.5 font-mono text-[12px] leading-5 text-slate-800',
          !expanded && 'line-clamp-4')}>
        {snippet}
      </pre>
      {(clamped || expanded) && (
        <div className="border-t border-slate-200 bg-white px-3">
          <button type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} aria-controls={codeId}
            className="h-9 text-[12.5px] font-medium text-primary-700 underline-offset-2 hover:underline">
            {expanded ? 'Show less' : 'Show all'}
          </button>
        </div>
      )}
    </div>
  );
}

function HelperLine({ children }) {
  return (
    <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-slate-500">
      <BookOpen size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

export default function WebsiteChatSection({
  config: configProp = null, onConfigChange, bare = false, hideWhenLocked = false, reloadKey = 0, className,
}) {
  const { user } = useAuth();
  const modules = useModules();
  const { pathname } = useLocation();
  const isSuper = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin';
  // Same gate the Chatbot page always used: granted per organisation, always on for super admins.
  const allowed = isSuper || modules.ai_chatbot === true;

  const [config, setConfig] = useState(configProp);
  const [loading, setLoading] = useState(!isSuper && !configProp);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (configProp) setConfig(configProp); }, [configProp]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/receptionist/my-config');
      setConfig(r.data || {});
    } catch (e) {
      setError(e?.response?.data?.message || "We couldn't load the website chat settings. Check your connection, then try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const hasConfigProp = !!configProp;
  useEffect(() => {
    if (!allowed || isSuper || hasConfigProp) { setLoading(false); return; }
    load();
  }, [allowed, isSuper, hasConfigProp, load, reloadKey]);

  // Saves the moment it is switched (as the Chatbot page always did), and puts it back on failure.
  const toggle = async (next) => {
    const before = config;
    setConfig((c) => ({ ...(c || {}), chatbot_enabled: next }));
    setSaving(true);
    try {
      const r = await api.put('/receptionist/my-config', { chatbot_enabled: next });
      const saved = r.data?.config;
      if (saved && typeof saved === 'object') {
        setConfig(saved);
        if (onConfigChange) onConfigChange(saved);
      }
      toast.success(next ? 'Website chat is on' : 'Website chat is off');
    } catch (e) {
      setConfig(before);
      toast.error(e?.response?.data?.message || 'Could not update website chat');
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    if (hideWhenLocked) return null;
    const locked = (
      <div className="flex items-start gap-3">
        <Lock size={16} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-slate-800">Website chat isn't enabled for your organisation</p>
          <p className="mt-0.5 text-[13px] text-slate-500">Contact your administrator to request access.</p>
        </div>
      </div>
    );
    return bare ? <div className={className}>{locked}</div> : (
      <SectionCard title="Website chat" icon={MessageCircle} className={className} bodyClass="px-5 py-4">{locked}</SectionCard>
    );
  }

  const enabled = !!config?.chatbot_enabled;
  const inSettings = pathname.startsWith('/settings');
  const inConsole = pathname.startsWith('/receptionist');
  const linkCls = 'font-medium text-primary-700 underline-offset-2 hover:underline';

  let body;
  if (isSuper) {
    body = (
      <div className="space-y-5">
        <p className="text-[13px] leading-relaxed text-slate-600">
          This is Rostery's own website chatbot. It uses the global widget, with no organisation key, and its
          settings live in the AI receptionist console.
        </p>
        <div>
          <p className="text-[13px] font-medium text-slate-700">Embed code</p>
          <p className="mb-2 mt-0.5 text-[12.5px] leading-relaxed text-slate-500">Paste this just before the closing &lt;/body&gt; tag on the website.</p>
          <EmbedCode snippet={GLOBAL_SNIPPET} />
        </div>
        <HelperLine>
          The Rostery chatbot shares its knowledge with the AI receptionist.{' '}
          {inConsole ? 'Edit it with the Settings button on this page.' : (
            <>Edit it in the <Link to="/receptionist" className={linkCls}>AI receptionist console</Link>.</>
          )}
        </HelperLine>
      </div>
    );
  } else if (loading) {
    body = <TableSkeleton rows={3} cols={2} />;
  } else if (error) {
    body = <ErrorState message={error} onRetry={load} />;
  } else {
    const snippet = embedSnippetFor(config?.widget_key);
    body = (
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800">Show the chat widget on your website</p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-slate-500">
              Visitors can chat with your receptionist, and each enquiry is saved as a web-chat lead.
              This saves as soon as you switch it.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 pt-0.5">
            {saving && <Loader2 size={14} className="animate-spin text-slate-400" aria-hidden="true" />}
            <Switch checked={enabled} onChange={toggle} disabled={saving} label="Show the chat widget on your website" />
          </div>
        </div>
        <div>
          <p className="text-[13px] font-medium text-slate-700">Embed code</p>
          <p className="mb-2 mt-0.5 text-[12.5px] leading-relaxed text-slate-500">
            Paste this just before the closing &lt;/body&gt; tag on your website.
            {enabled ? '' : ' It only works while website chat is on.'}
          </p>
          {snippet ? <EmbedCode snippet={snippet} /> : (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-800">
              Turn on website chat to get your embed code.
            </p>
          )}
        </div>
        <HelperLine>
          Website chat uses the same knowledge as your AI receptionist.{' '}
          {inSettings ? 'Edit it in the knowledge section on this page.' : isAdmin ? (
            <>Edit it in <Link to="/settings/ai-receptionist" className={linkCls}>Settings → AI receptionist</Link>.</>
          ) : 'An admin can edit it in Settings → AI receptionist.'}
        </HelperLine>
      </div>
    );
  }

  if (bare) return <div className={className}>{body}</div>;
  return (
    <SectionCard title="Website chat" icon={MessageCircle} className={className} bodyClass="px-5 py-4"
      actions={!isSuper && config && !loading && !error
        ? <StatusPill status={enabled ? 'active' : 'inactive'} label={enabled ? 'On' : 'Off'} />
        : null}>
      {body}
    </SectionCard>
  );
}
